import { Component, EventEmitter, Input, OnInit, OnChanges, Output, HostListener } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { faPlus, faTrash, faTimes, faGripVertical, faEdit, faCheck, faExclamationTriangle, faCopy, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { catchError, of } from 'rxjs';
import { AnamneseCustomQuestionsService, IClinicQuestionsResponse } from 'src/app/services/anamnese-custom-questions.service';
import { CompanyService } from 'src/app/services/company.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { ICustomAnamneseQuestion, ICustomQuestionOption, ISectionWithCustomQuestions, ISectionFromAPI } from 'src/app/models/custom-anamnese-question.model';
import { AnamneseQuestionType } from 'src/app/models/anamnese-form-question.model';
import { ICompany } from 'src/app/models/company.model';

@Component({
  selector: 'app-anamnese-custom-questions-modal',
  templateUrl: './anamnese-custom-questions-modal.component.html',
  styleUrls: ['./anamnese-custom-questions-modal.component.sass']
})
export class AnamneseCustomQuestionsModalComponent implements OnInit, OnChanges {
  @Input() isOpen = false;
  @Input() companyCnpj = '';
  @Output() closeModal = new EventEmitter<void>();

  // FontAwesome icons
  faPlus = faPlus;
  faTrash = faTrash;
  faTimes = faTimes;
  faGripVertical = faGripVertical;
  faEdit = faEdit;
  faCheck = faCheck;
  faExclamationTriangle = faExclamationTriangle;
  faCopy = faCopy;
  faBuilding = faBuilding;

  loading = false;
  publishLoading = false;
  sections: ISectionWithCustomQuestions[] = [];
  questionTypes: { value: AnamneseQuestionType; label: string }[] = [
    { value: 'TEXT_INPUT', label: 'Texto' },
    { value: 'SINGLE_CHOICE', label: 'Escolha Única' },
    { value: 'MULTIPLE_CHOICE', label: 'Múltipla Escolha' },
    { value: 'YES_NO', label: 'Sim/Não' },
    { value: 'DATE', label: 'Data' },
    { value: 'TIME', label: 'Hora' }
  ];

  // Confirmação de publicação
  showPublishConfirmation = false;
  
  // Confirmação de exclusão
  showDeleteConfirmation = false;
  questionToDelete: { question: ICustomAnamneseQuestion; sectionIndex: number; questionIndex: number } | null = null;

  // Confirmação de fechamento
  showCloseConfirmation = false;

  // Confirmação de limpeza completa
  showClearConfirmation = false;

  // Modal de erros de validação
  showValidationErrors = false;
  validationErrorMessages: string[] = [];

  // Modal de cópia entre clínicas
  showClinicSelectionModal = false;
  showCopyConfirmation = false;
  loadingCompanies = false;
  copyLoading = false;
  clinicSearchTerm = '';
  allCompanies: ICompany[] = [];
  filteredCompanies: ICompany[] = [];
  selectedSourceClinic: ICompany | null = null;

  constructor(
    private anamneseCustomQuestionsService: AnamneseCustomQuestionsService,
    private companyService: CompanyService,
    private store: Store
  ) {}

  @HostListener('keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    // Fechar modal de validação se estiver aberto
    if (this.showValidationErrors) {
      this.closeValidationErrors();
      return;
    }

    if (this.showCopyConfirmation) {
      this.cancelCopy();
      return;
    }

    if (this.showClinicSelectionModal) {
      this.cancelClinicSelection();
      return;
    }
    
    // Só fechar se não houver modais de confirmação abertos
    if (!this.showPublishConfirmation && !this.showDeleteConfirmation && !this.showCloseConfirmation && !this.showClearConfirmation) {
      this.close();
    }
  }

  ngOnInit(): void {
    if (this.isOpen && this.companyCnpj) {
      this.loadData();
    }
  }

  ngOnChanges(): void {
    if (this.isOpen && this.companyCnpj) {
      this.loadData();
    }
  }

  loadData(): void {
    this.loading = true;
    
    // Carregar seções e perguntas da clínica
    this.anamneseCustomQuestionsService.getSections()
      .pipe(
        catchError(error => {
          this.store.dispatch(setWarningText('Erro ao carregar seções da anamnese'));
          return of([]);
        })
      )
      .subscribe((sections: ISectionFromAPI[]) => {
        // Carregar perguntas customizadas da clínica
        this.anamneseCustomQuestionsService.getClinicQuestions(this.companyCnpj)
          .pipe(
            catchError(error => {
              console.warn('Nenhuma pergunta customizada encontrada ou erro ao carregar', error);
              return of(null);
            })
          )
          .subscribe((clinicResponse: IClinicQuestionsResponse | null) => {
            // Combinar seções gerais com perguntas customizadas
            this.sections = sections
              .sort((a, b) => (a.order || 0) - (b.order || 0)) // Ordenar seções pelo campo order
              .map((section, index) => ({
                id: section.id,
                name: section.label || section.name || 'Seção sem nome',
                order: section.order || index + 1,
                questions: this.getCustomQuestionsForSection(clinicResponse, section.id),
                expanded: true
              }));
            
            this.loading = false;
          });
      });
  }

  private getCustomQuestionsForSection(clinicResponse: IClinicQuestionsResponse | null, sectionId: string): ICustomAnamneseQuestion[] {
    // Se não há resposta da clínica ou não há perguntas, retorna array vazio
    if (!clinicResponse || !clinicResponse.questions || !Array.isArray(clinicResponse.questions)) {
      return [];
    }

    // Filtrar perguntas que pertencem a esta seção
    const sectionQuestions = clinicResponse.questions.filter(q => q.section_id === sectionId);

    // Ordenar perguntas pelo campo order_in_section
    const sortedQuestions = sectionQuestions.sort((a, b) => a.order_in_section - b.order_in_section);

    return sortedQuestions.map((q) => ({
      id: q.id,
      label: q.label,
      order_in_section: q.order_in_section,
      is_required: q.is_required,
      type: q.type as AnamneseQuestionType,
      help_text: q.help_text,
      options: q.options ? q.options
        .sort((a, b) => (a.order || 0) - (b.order || 0)) // Ordenar opções também
        .map((opt) => ({
          id: opt.id,
          value: opt.value,
          label: opt.label,
          order: opt.order || 0,
          triggersFreeText: opt.triggersFreeText,
          isEditing: false,
          isNew: false
        })) : [],
      section_id: sectionId,
      isEditing: false,
      isNew: false
    }));
  }

  toggleSection(sectionIndex: number): void {
    this.sections[sectionIndex].expanded = !this.sections[sectionIndex].expanded;
  }

  addQuestion(sectionIndex: number): void {
    const newQuestion: ICustomAnamneseQuestion = {
      label: '',
      order_in_section: this.sections[sectionIndex].questions.length + 1,
      is_required: false,
      type: 'TEXT_INPUT',
      help_text: '',
      options: [],
      section_id: this.sections[sectionIndex].id,
      isEditing: true,
      isNew: true
    };

    this.sections[sectionIndex].questions.push(newQuestion);
  }

  editQuestion(sectionIndex: number, questionIndex: number): void {
    this.sections[sectionIndex].questions[questionIndex].isEditing = true;
  }

  saveQuestion(sectionIndex: number, questionIndex: number): void {
    const question = this.sections[sectionIndex].questions[questionIndex];
    
    // Validação básica
    if (!question.label.trim()) {
      this.store.dispatch(setWarningText('O texto da pergunta é obrigatório'));
      return;
    }

    // Se for SINGLE_CHOICE ou MULTIPLE_CHOICE, deve ter pelo menos uma opção
    if ((question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE') && 
        (!question.options || question.options.length === 0)) {
      this.store.dispatch(setWarningText('Perguntas de escolha devem ter pelo menos uma opção'));
      return;
    }

    // Salvar automaticamente todas as opções em edição antes de salvar a pergunta
    if (question.options && question.options.length > 0) {
      const optionValidationErrors: string[] = [];
      
      for (let optionIndex = 0; optionIndex < question.options.length; optionIndex++) {
        const option = question.options[optionIndex];
        
        if (option.isEditing) {
          const optionValidation = this.validateAndSaveOption(sectionIndex, questionIndex, optionIndex);
          if (!optionValidation.success) {
            // Extrair apenas a parte relevante da mensagem de erro
            const errorMessage = optionValidation.message.includes(': ') 
              ? optionValidation.message.split(': ')[1] 
              : optionValidation.message;
            optionValidationErrors.push(`Opção ${optionIndex + 1}: ${errorMessage}`);
          }
        }
      }
      
      // Se há erros nas opções, mostrar e não salvar a pergunta
      if (optionValidationErrors.length > 0) {
        this.store.dispatch(setWarningText(`Corrija as opções antes de salvar:\n• ${optionValidationErrors.join('\n• ')}`));
        return;
      }
    }

    // Se chegou até aqui, salvar a pergunta
    question.isEditing = false;
    question.isNew = false;
  }

  cancelEditQuestion(sectionIndex: number, questionIndex: number): void {
    const question = this.sections[sectionIndex].questions[questionIndex];
    
    if (question.isNew) {
      // Remove a pergunta se era nova e foi cancelada
      this.sections[sectionIndex].questions.splice(questionIndex, 1);
    } else {
      // Volta para o modo de visualização
      question.isEditing = false;
    }
  }

  confirmDeleteQuestion(sectionIndex: number, questionIndex: number): void {
    const question = this.sections[sectionIndex].questions[questionIndex];
    this.questionToDelete = { question, sectionIndex, questionIndex };
    this.showDeleteConfirmation = true;
  }

  deleteQuestion(): void {
    if (this.questionToDelete) {
      this.sections[this.questionToDelete.sectionIndex].questions.splice(this.questionToDelete.questionIndex, 1);
      this.reorderQuestions(this.questionToDelete.sectionIndex);
    }
    this.showDeleteConfirmation = false;
    this.questionToDelete = null;
  }

  cancelDeleteQuestion(): void {
    this.showDeleteConfirmation = false;
    this.questionToDelete = null;
  }

  /**
   * Remove todas as perguntas de todas as seções.
   * Equivale a excluir cada pergunta manualmente, mas em uma única ação.
   */
  clearAllQuestions(): void {
    // Caso algum modal de exclusão individual esteja aberto, fechar
    this.showDeleteConfirmation = false;
    this.questionToDelete = null;

    // Limpar perguntas de todas as seções
    for (const section of this.sections) {
      section.questions = [];
    }
  }

  // Modal de confirmação da limpeza
  showClearAllModal(): void {
    this.showClearConfirmation = true;
  }

  cancelClearAll(): void {
    this.showClearConfirmation = false;
  }

  confirmClearAll(): void {
    this.clearAllQuestions();
    this.showClearConfirmation = false;
  }

  addOption(sectionIndex: number, questionIndex: number): void {
    const question = this.sections[sectionIndex].questions[questionIndex];
    if (!question.options) {
      question.options = [];
    }

    const newOption: ICustomQuestionOption = {
      value: '',
      label: '',
      order: question.options.length + 1,
      triggersFreeText: false,
      isEditing: true,
      isNew: true
    };

    question.options.push(newOption);
  }

  onOptionLabelChange(sectionIndex: number, questionIndex: number, optionIndex: number, newLabel: string): void {
    const option = this.sections[sectionIndex].questions[questionIndex].options![optionIndex];
    option.label = newLabel;
    
    // O valor será igual ao label
    option.value = newLabel;
  }

  saveOption(sectionIndex: number, questionIndex: number, optionIndex: number): void {
    const option = this.sections[sectionIndex].questions[questionIndex].options![optionIndex];
    
    if (!option.label.trim()) {
      this.store.dispatch(setWarningText('O texto da opção é obrigatório'));
      return;
    }

    // O valor será igual ao label
    option.value = option.label;

    option.isEditing = false;
    option.isNew = false;
  }

  cancelEditOption(sectionIndex: number, questionIndex: number, optionIndex: number): void {
    const option = this.sections[sectionIndex].questions[questionIndex].options![optionIndex];
    
    if (option.isNew) {
      this.sections[sectionIndex].questions[questionIndex].options!.splice(optionIndex, 1);
    } else {
      option.isEditing = false;
    }
  }

  deleteOption(sectionIndex: number, questionIndex: number, optionIndex: number): void {
    this.sections[sectionIndex].questions[questionIndex].options!.splice(optionIndex, 1);
    this.reorderOptions(sectionIndex, questionIndex);
  }

  moveQuestionUp(sectionIndex: number, questionIndex: number): void {
    if (questionIndex > 0) {
      const questions = this.sections[sectionIndex].questions;
      [questions[questionIndex], questions[questionIndex - 1]] = [questions[questionIndex - 1], questions[questionIndex]];
      this.reorderQuestions(sectionIndex);
    }
  }

  moveQuestionDown(sectionIndex: number, questionIndex: number): void {
    const questions = this.sections[sectionIndex].questions;
    if (questionIndex < questions.length - 1) {
      [questions[questionIndex], questions[questionIndex + 1]] = [questions[questionIndex + 1], questions[questionIndex]];
      this.reorderQuestions(sectionIndex);
    }
  }

  moveOptionUp(sectionIndex: number, questionIndex: number, optionIndex: number): void {
    const options = this.sections[sectionIndex].questions[questionIndex].options!;
    if (optionIndex > 0) {
      [options[optionIndex], options[optionIndex - 1]] = [options[optionIndex - 1], options[optionIndex]];
      this.reorderOptions(sectionIndex, questionIndex);
    }
  }

  moveOptionDown(sectionIndex: number, questionIndex: number, optionIndex: number): void {
    const options = this.sections[sectionIndex].questions[questionIndex].options!;
    if (optionIndex < options.length - 1) {
      [options[optionIndex], options[optionIndex + 1]] = [options[optionIndex + 1], options[optionIndex]];
      this.reorderOptions(sectionIndex, questionIndex);
    }
  }

  private reorderQuestions(sectionIndex: number): void {
    this.sections[sectionIndex].questions.forEach((question, index) => {
      question.order_in_section = index + 1;
    });
  }

  private reorderOptions(sectionIndex: number, questionIndex: number): void {
    const options = this.sections[sectionIndex].questions[questionIndex].options;
    if (options) {
      options.forEach((option, index) => {
        option.order = index + 1;
      });
    }
  }

  onQuestionTypeChange(sectionIndex: number, questionIndex: number): void {
    const question = this.sections[sectionIndex].questions[questionIndex];
    
    // Se mudou para um tipo que não precisa de opções, limpar as opções
    if (question.type === 'TEXT_INPUT' || question.type === 'YES_NO') {
      question.options = [];
    }
    // Se mudou para um tipo que precisa de opções e não tem, adicionar uma opção padrão
    else if ((question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE') && 
             (!question.options || question.options.length === 0)) {
      const defaultLabel = 'Opção 1';
      question.options = [{
        value: defaultLabel,
        label: defaultLabel,
        order: 1,
        triggersFreeText: false,
        isEditing: true,
        isNew: true
      }];
    }
  }

  showPublishModal(): void {
    // Primeiro, salvar automaticamente todas as perguntas e opções em edição
    const saveResult = this.autoSaveAllChanges();
    
    if (!saveResult.success) {
      this.store.dispatch(setWarningText(saveResult.message));
      return;
    }

    // Se chegou até aqui, tudo foi salvo com sucesso
    this.showPublishConfirmation = true;
  }

  /**
   * Salva automaticamente todas as perguntas e opções em edição
   * Retorna objeto com status de sucesso e mensagem de erro se houver
   */
  autoSaveAllChanges(): { success: boolean; message: string } {
    const validationErrors: string[] = [];

    for (let sectionIndex = 0; sectionIndex < this.sections.length; sectionIndex++) {
      const section = this.sections[sectionIndex];
      
      for (let questionIndex = 0; questionIndex < section.questions.length; questionIndex++) {
        const question = section.questions[questionIndex];
        
        // Se a pergunta está em edição, tentar salvá-la
        if (question.isEditing) {
          const questionValidation = this.validateAndSaveQuestion(sectionIndex, questionIndex);
          if (!questionValidation.success) {
            validationErrors.push(`Seção "${section.name}" - ${questionValidation.message}`);
            continue; // Não continuar com as opções se a pergunta tem erro
          }
        }

        // Verificar opções em edição
        if (question.options) {
          for (let optionIndex = 0; optionIndex < question.options.length; optionIndex++) {
            const option = question.options[optionIndex];
            
            if (option.isEditing) {
              const optionValidation = this.validateAndSaveOption(sectionIndex, questionIndex, optionIndex);
              if (!optionValidation.success) {
                validationErrors.push(`Seção "${section.name}" - Pergunta "${question.label}" - ${optionValidation.message}`);
              }
            }
          }
        }
      }
    }

    if (validationErrors.length > 0) {
      // Mostrar modal customizado com erros
      this.validationErrorMessages = validationErrors;
      this.showValidationErrors = true;
      
      return {
        success: false,
        message: 'Existem campos obrigatórios em branco'
      };
    }

    return { success: true, message: '' };
  }

  /**
   * Valida e salva uma pergunta específica
   */
  validateAndSaveQuestion(sectionIndex: number, questionIndex: number): { success: boolean; message: string } {
    const question = this.sections[sectionIndex].questions[questionIndex];
    
    // Validar campo obrigatório: texto da pergunta
    if (!question.label || !question.label.trim()) {
      return {
        success: false,
        message: `Pergunta ${questionIndex + 1}: O texto da pergunta é obrigatório`
      };
    }

    // Validar se perguntas de escolha têm opções
    if ((question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE')) {
      if (!question.options || question.options.length === 0) {
        return {
          success: false,
          message: `Pergunta "${question.label}": Perguntas de escolha devem ter pelo menos uma opção`
        };
      }

      // Verificar se todas as opções estão válidas
      for (let i = 0; i < question.options.length; i++) {
        const option = question.options[i];
        if (!option.label || !option.label.trim()) {
          return {
            success: false,
            message: `Pergunta "${question.label}": Opção ${i + 1} está sem texto`
          };
        }
      }
    }

    // Se chegou até aqui, salvar a pergunta
    question.isEditing = false;
    question.isNew = false;
    
    return { success: true, message: '' };
  }

  /**
   * Valida e salva uma opção específica
   */
  validateAndSaveOption(sectionIndex: number, questionIndex: number, optionIndex: number): { success: boolean; message: string } {
    const option = this.sections[sectionIndex].questions[questionIndex].options![optionIndex];
    
    // Validar campo obrigatório: texto da opção
    if (!option.label || !option.label.trim()) {
      return {
        success: false,
        message: `Opção ${optionIndex + 1}: O texto da opção é obrigatório`
      };
    }

    // O valor será igual ao label
    option.value = option.label;

    // Se chegou até aqui, salvar a opção
    option.isEditing = false;
    option.isNew = false;
    
    return { success: true, message: '' };
  }

  publishQuestions(): void {
    this.publishLoading = true;
    
    // Preparar dados para envio
    const allQuestions = this.sections.reduce((acc, section) => {
      const sectionQuestions = section.questions.map(question => ({
        label: question.label,
        order_in_section: question.order_in_section,
        is_required: question.is_required,
        type: question.type,
        help_text: question.help_text,
        section_id: section.id,
        options: question.options ? question.options.map(opt => ({
          value: opt.value,
          label: opt.label,
          order: opt.order,
          triggersFreeText: opt.triggersFreeText
        })) : undefined
      }));
      
      return [...acc, ...sectionQuestions];
    }, [] as any[]);

    this.anamneseCustomQuestionsService.publishClinicQuestions(this.companyCnpj, { questions: allQuestions })
      .pipe(
        catchError(error => {
          this.store.dispatch(setWarningText('Erro ao publicar perguntas da anamnese'));
          this.publishLoading = false;
          return of(null);
        })
      )
      .subscribe(response => {
        if (response) {
          this.publishLoading = false;
          this.showPublishConfirmation = false;
          this.close();
        }
      });
  }

  cancelPublish(): void {
    this.showPublishConfirmation = false;
  }

  closeValidationErrors(): void {
    this.showValidationErrors = false;
    this.validationErrorMessages = [];
  }

  showCopyFromClinicModal(): void {
    if (!this.companyCnpj) {
      this.store.dispatch(setWarningText('Não foi possível identificar a clínica atual.'));
      return;
    }

    this.showClinicSelectionModal = true;
    this.showCopyConfirmation = false;
    this.copyLoading = false;
    this.selectedSourceClinic = null;
    this.clinicSearchTerm = '';

    if (this.allCompanies.length === 0) {
      this.loadAllCompanies();
    } else {
      this.filteredCompanies = [...this.allCompanies];
    }
  }

  private loadAllCompanies(): void {
    this.loadingCompanies = true;

    this.companyService.listCompanies(0, 99999, 'ASC', 'corporateName')
      .pipe(
        catchError(() => {
          this.loadingCompanies = false;
          this.store.dispatch(setWarningText('Erro ao carregar a lista de clínicas.'));
          return of(null);
        })
      )
      .subscribe(response => {
        if (!response) {
          return;
        }

        const companies = response.data || [];
        this.allCompanies = companies.filter((company: ICompany) => company.cnpj !== this.companyCnpj);
        this.filteredCompanies = [...this.allCompanies];
        this.loadingCompanies = false;
      });
  }

  filterCompanies(searchTerm: string): void {
    this.clinicSearchTerm = searchTerm;
    const normalizedTerm = searchTerm.trim().toLowerCase();

    if (!normalizedTerm) {
      this.filteredCompanies = [...this.allCompanies];
      return;
    }

    this.filteredCompanies = this.allCompanies.filter(company => {
      const name = (company.corporate_name || '').toLowerCase();
      const cnpj = (company.cnpj || '').toLowerCase();
      return name.includes(normalizedTerm) || cnpj.includes(normalizedTerm);
    });
  }

  selectSourceClinic(company: ICompany): void {
    this.selectedSourceClinic = company;
  }

  cancelClinicSelection(): void {
    this.showClinicSelectionModal = false;
    this.selectedSourceClinic = null;
    this.clinicSearchTerm = '';
  }

  openCopyConfirmationModal(): void {
    if (!this.selectedSourceClinic) {
      return;
    }

    this.showClinicSelectionModal = false;
    this.showCopyConfirmation = true;
  }

  cancelCopy(): void {
    this.showCopyConfirmation = false;
    this.showClinicSelectionModal = true;
    this.copyLoading = false;
  }

  confirmCopyFromClinic(): void {
    if (!this.selectedSourceClinic || !this.companyCnpj) {
      return;
    }

    this.copyLoading = true;

    this.anamneseCustomQuestionsService.copyClinicQuestions(this.selectedSourceClinic.cnpj, this.companyCnpj)
      .pipe(
        catchError(() => {
          this.copyLoading = false;
          this.store.dispatch(setWarningText('Erro ao copiar perguntas da clínica selecionada.'));
          return of({ copyError: true });
        })
      )
      .subscribe(result => {
        if ((result as { copyError?: boolean } | null)?.copyError) {
          return;
        }

        this.copyLoading = false;
        this.showCopyConfirmation = false;
        this.showClinicSelectionModal = false;
        this.selectedSourceClinic = null;
        this.loadData();
        this.store.dispatch(setWarningText('Perguntas copiadas com sucesso.', 200));
      });
  }

  close(): void {
    // Verificar se há alterações não salvas
    if (this.hasUnsavedChanges()) {
      this.showCloseConfirmation = true;
    } else {
      this.forceClose();
    }
  }

  confirmClose(): void {
    this.showCloseConfirmation = false;
    this.forceClose();
  }

  cancelClose(): void {
    this.showCloseConfirmation = false;
  }

  forceClose(): void {
    // Limpar dados e fechar modal
    this.sections = [];
    this.showPublishConfirmation = false;
    this.showDeleteConfirmation = false;
    this.showCloseConfirmation = false;
    this.showValidationErrors = false;
    this.validationErrorMessages = [];
    this.questionToDelete = null;
    this.closeModal.emit();
  }

  hasUnsavedChanges(): boolean {
    return this.sections.some(section => 
      section.questions.some(question => 
        question.isEditing || question.isNew ||
        (question.options && question.options.some(opt => opt.isEditing || opt.isNew))
      )
    );
  }

  getQuestionTypeLabel(type: AnamneseQuestionType): string {
    const typeConfig = this.questionTypes.find(t => t.value === type);
    return typeConfig ? typeConfig.label : type;
  }

  hasOptionsInEditing(question: ICustomAnamneseQuestion): boolean {
    return question.options ? question.options.some(opt => opt.isEditing) : false;
  }
}
